"use client"

import { HeroCarouselSlideDto } from "api"
import { SyntheticEvent, useEffect, useRef, useState } from "react"
import {
  HeroBg,
  HeroBgImageDesktop,
  HeroBgImageMobile,
  HeroBgOverlay,
  HeroBgVideoDesktop,
  HeroBgVideoMobile,
} from "./hero.styled"

type Props = {
  slide: HeroCarouselSlideDto
  isActive: boolean
  shouldPlayVideo: boolean
  reducedMotion: boolean
  canAdvance: boolean
  onVideoEnded: () => void
  onVideoDuration: (seconds: number) => void
}

// Both <video> elements stay mounted and keep decoding — the breakpoint hiding
// is CSS-only. Only the one this breakpoint actually shows may drive carousel
// timing, or `ended` fires twice and the carousel skips a slide (DLK-949).
// display:none collapses offsetWidth to 0.
const isVisible = (el: HTMLVideoElement) => el.offsetWidth > 0

const HeroBackground = ({
  slide,
  isActive,
  shouldPlayVideo,
  reducedMotion,
  canAdvance,
  onVideoEnded,
  onVideoDuration,
}: Props) => {
  // Desktop and mobile each get their own ref. Both <video> elements may be
  // mounted (one hidden per breakpoint via CSS), so a single shared ref would
  // only ever point at the last-mounted element — leaving the visible video
  // playing when paused (DLK-742). Control both refs to cover either view.
  const desktopVideoRef = useRef<HTMLVideoElement>(null)
  const mobileVideoRef = useRef<HTMLVideoElement>(null)
  const [videoFailed, setVideoFailed] = useState(false)

  // Media is resolved per breakpoint independently — desktop and mobile may use
  // different media types (DLK-797). The previous single `hasVideo` flag forced
  // both breakpoints into the same branch, so a video/image mismatch broke one
  // of them (blank desktop, or the desktop video leaking onto mobile).
  // Per platform: prefer video over image when both exist. Mobile only falls
  // back to the desktop media when it has no media of its own.
  const desktopImage = slide.backgroundImage
  const desktopVideo = slide.backgroundVideo?.imageUrl
  const desktopUsesVideo = !!desktopVideo && !videoFailed

  const mobileHasOwnMedia =
    !!slide.backgroundVideoMobile?.imageUrl ||
    !!slide.backgroundImageMobile?.imageUrl
  const mobileVideo = mobileHasOwnMedia
    ? slide.backgroundVideoMobile?.imageUrl
    : desktopVideo
  const mobileImage = mobileHasOwnMedia
    ? slide.backgroundImageMobile
    : desktopImage
  const mobileUsesVideo = !!mobileVideo && !videoFailed

  // Rewind to frame 0 every time this slide becomes active. Swiper keeps every
  // slide mounted, so a <video> paused mid-play holds that position for the rest
  // of the session — on the next pass it resumed near the end and `loop` snapped
  // it back to the start (DLK-949). Declared above the play/pause effect so the
  // seek lands before playback resumes. Keyed to isActive rather than
  // shouldPlayVideo so the play/pause toggle still resumes where it left off.
  useEffect(() => {
    if (!isActive) return
    const videos = [desktopVideoRef.current, mobileVideoRef.current]
    videos.forEach((v) => {
      // readyState 0 means nothing is buffered and currentTime is already 0;
      // seeking that early is a no-op at best and throws in Safari.
      if (v && v.readyState > 0) v.currentTime = 0
    })
  }, [isActive])

  useEffect(() => {
    const videos = [desktopVideoRef.current, mobileVideoRef.current]
    videos.forEach((v) => {
      if (!v) return
      if (shouldPlayVideo) v.play().catch(() => {})
      else v.pause()
    })
  }, [shouldPlayVideo, desktopUsesVideo, mobileUsesVideo])

  const handleEnded = (e: SyntheticEvent<HTMLVideoElement>) => {
    if (!isActive || !isVisible(e.currentTarget)) return
    onVideoEnded()
  }

  const handleLoadedMetadata = (e: SyntheticEvent<HTMLVideoElement>) => {
    const { duration } = e.currentTarget
    if (!isVisible(e.currentTarget) || !Number.isFinite(duration)) return
    onVideoDuration(duration)
  }

  return (
    <HeroBg aria-hidden>
      {desktopUsesVideo ? (
        <HeroBgVideoDesktop
          ref={desktopVideoRef}
          src={desktopVideo}
          muted
          // A single-slide carousel has nowhere to advance to, so it keeps
          // looping; otherwise `ended` must fire to hand off to the next slide.
          loop={!canAdvance}
          playsInline
          autoPlay={!reducedMotion}
          poster={desktopImage?.imageUrl}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={handleEnded}
          onError={() => setVideoFailed(true)}
        />
      ) : (
        desktopImage?.imageUrl && (
          <HeroBgImageDesktop
            src={desktopImage.imageUrl}
            alt={desktopImage.alt ?? ""}
          />
        )
      )}
      {mobileUsesVideo ? (
        <HeroBgVideoMobile
          ref={mobileVideoRef}
          src={mobileVideo}
          muted
          loop={!canAdvance}
          playsInline
          autoPlay={!reducedMotion}
          poster={mobileImage?.imageUrl}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={handleEnded}
          onError={() => setVideoFailed(true)}
        />
      ) : (
        mobileImage?.imageUrl && (
          <HeroBgImageMobile
            src={mobileImage.imageUrl}
            alt={mobileImage.alt ?? ""}
          />
        )
      )}
      <HeroBgOverlay />
    </HeroBg>
  )
}

export default HeroBackground
